import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  getAllUsers,
  getUsersByType,
  getUserByEmail,
  updateUserStatus,
  UserDTO,
  UserType,
  UserStatus,
} from '../services/userManagementService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<UserStatus>('ACTIVE');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState<UserDTO | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const userTypeTabs: { label: string; type: UserType | 'ALL' }[] = [
    { label: 'All Users', type: 'ALL' },
    { label: 'Teachers', type: 'TEACHER' },
    { label: 'Tutors', type: 'TUTOR' },
    { label: 'System Admins', type: 'SYSTEM_ADMIN' },
    { label: 'School Admins', type: 'SCHOOL_ADMIN' },
  ];

  useEffect(() => {
    loadUsers();
  }, [tabValue]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (tabValue === 0) {
        // Load all users (non-STUDENT/PARENT)
        const allUsers = await getAllUsers();
        setUsers(allUsers);
      } else {
        // Load users by type
        const type = userTypeTabs[tabValue].type as UserType;
        const usersByType = await getUsersByType(type);
        setUsers(usersByType);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserDTO) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleStatusChange = (status: UserStatus) => {
    if (!selectedUser) return;
    setNewStatus(status);
    setStatusChangeReason('');
    setStatusDialogOpen(true);
    handleMenuClose();
  };

  const confirmStatusChange = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await updateUserStatus(selectedUser.id, newStatus);
      setSuccessMessage(
        `User ${selectedUser.firstName} ${selectedUser.lastName} status changed to ${newStatus}`
      );
      setStatusDialogOpen(false);
      loadUsers();
      
      // Clear search result if it was the updated user
      if (searchResult?.id === selectedUser.id) {
        setSearchResult(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user status');
    }
  };

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      const user = await getUserByEmail(searchEmail.trim());
      setSearchResult(user);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'User not found');
      setSearchResult(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'warning';
      case 'SUSPENDED':
        return 'error';
      case 'PENDING':
        return 'info';
      case 'DELETED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircleIcon fontSize="small" />;
      case 'INACTIVE':
      case 'SUSPENDED':
      case 'DELETED':
        return <BlockIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getAvailableStatusTransitions = (currentStatus: string): UserStatus[] => {
    switch (currentStatus) {
      case 'PENDING':
        return ['ACTIVE', 'DELETED'];
      case 'ACTIVE':
        return ['INACTIVE', 'SUSPENDED', 'DELETED'];
      case 'INACTIVE':
        return ['ACTIVE', 'DELETED'];
      case 'SUSPENDED':
        return ['ACTIVE', 'DELETED'];
      case 'DELETED':
        return []; // Terminal state
      default:
        return [];
    }
  };

  const canChangeStatus = (user: UserDTO, newStatus: UserStatus): boolean => {
    if (user.status === newStatus) return false;
    if (user.status === 'DELETED') return false;
    return getAvailableStatusTransitions(user.status).includes(newStatus);
  };

  const renderUserRow = (user: UserDTO) => (
    <TableRow key={user.id} hover>
      <TableCell>{user.id}</TableCell>
      <TableCell>
        {user.firstName} {user.lastName}
      </TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <Chip
          label={user.userType}
          size="small"
          variant="outlined"
          color="primary"
        />
      </TableCell>
      <TableCell>
        <Chip
          icon={getStatusIcon(user.status)}
          label={user.status}
          size="small"
          color={getStatusColor(user.status)}
        />
      </TableCell>
      <TableCell>
        {user.emailVerified ? (
          <Chip label="Verified" size="small" color="success" />
        ) : (
          <Chip label="Not Verified" size="small" color="warning" />
        )}
      </TableCell>
      <TableCell>
        {user.lastLoginAt
          ? new Date(user.lastLoginAt).toLocaleDateString()
          : 'Never'}
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, user)}
          disabled={user.status === 'DELETED'}
        >
          <MoreVertIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  if (loading && users.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadUsers}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search by Email */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search User by Email
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Email Address"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searchLoading || !searchEmail.trim()}
          >
            {searchLoading ? <CircularProgress size={24} /> : 'Search'}
          </Button>
        </Box>
        {searchResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Found User: {searchResult.firstName} {searchResult.lastName}
              </Typography>
              <Typography variant="body2">
                Status: {searchResult.status} | Type: {searchResult.userType}
              </Typography>
            </Alert>
            <Box sx={{ mt: 1 }}>
              {renderUserRow(searchResult)}
            </Box>
          </Box>
        )}
      </Paper>

      {/* User Type Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {userTypeTabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email Verified</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => renderUserRow(user))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Status Change Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {selectedUser && (
          <>
            {canChangeStatus(selectedUser, 'ACTIVE') && (
              <MenuItem onClick={() => handleStatusChange('ACTIVE')}>
                <CheckCircleIcon sx={{ mr: 1 }} />
                Activate User
              </MenuItem>
            )}
            {canChangeStatus(selectedUser, 'INACTIVE') && (
              <MenuItem onClick={() => handleStatusChange('INACTIVE')}>
                <BlockIcon sx={{ mr: 1 }} />
                Deactivate User
              </MenuItem>
            )}
            {canChangeStatus(selectedUser, 'SUSPENDED') && (
              <MenuItem onClick={() => handleStatusChange('SUSPENDED')}>
                <BlockIcon sx={{ mr: 1 }} />
                Suspend User
              </MenuItem>
            )}
            {canChangeStatus(selectedUser, 'DELETED') && (
              <MenuItem onClick={() => handleStatusChange('DELETED')}>
                <DeleteIcon sx={{ mr: 1 }} />
                Delete User (Soft Delete)
              </MenuItem>
            )}
            {!getAvailableStatusTransitions(selectedUser.status).length && (
              <MenuItem disabled>
                No available status transitions
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change User Status
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <>
              <Typography variant="body1" gutterBottom>
                User: <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email: {selectedUser.email}
              </Typography>
              <Box sx={{ my: 2 }}>
                <Typography variant="body2">
                  Current Status: <Chip label={selectedUser.status} size="small" color={getStatusColor(selectedUser.status)} />
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  New Status: <Chip label={newStatus} size="small" color={getStatusColor(newStatus)} />
                </Typography>
              </Box>
              <TextField
                label="Reason for Status Change (Optional)"
                fullWidth
                multiline
                rows={3}
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                placeholder="e.g., Policy violation, Security concern, Account closure request..."
                sx={{ mt: 2 }}
              />
              {newStatus === 'DELETED' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This will perform a soft delete. The user account will be marked as deleted but data will be retained for compliance purposes.
                </Alert>
              )}
              {newStatus === 'SUSPENDED' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  User will be temporarily suspended. They can be reactivated later.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmStatusChange}
            variant="contained"
            color={newStatus === 'DELETED' ? 'error' : 'primary'}
          >
            Confirm Status Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  );
};

export default UserManagement;

